import React, { useEffect, useContext, useState } from "react";
import {
    DatePicker, Form, Input, Modal, Select, Tag,
    message, Button, Row, Space, Col, notification
} from "antd";
import { useAtom } from "jotai";
import { EditOutlined, CalendarOutlined, CheckOutlined } from "@ant-design/icons";
import {
	visibleModalCrearSolicitudAusenciaAtom,
    ocultarFechasSolicitadasAtom,
    solicitudesAusenciaAtom
} from "../../ProgramarMovimientosStore";
import moment from "moment";
import {
	useListarTiposAusencia,
	useObtenerAprobadorPrimerNivel,
	useObtenerDetalleVacacionesColaborador,
	useObtenerJefeInmediato,
	useSeleccionarTipoAusencia,
    useSimularDescuentoVacaciones,
} from "../../hooks";
import { useDispositivo } from "hooks";
import { useBloqueoMovimientos, AvisoBloqueoMovimientos } from "../../../../../common/bloqueo-movimientos";
import Control from "./Control";
import Archivo from "./Archivo";
import { SecurityContext } from "context/SecurityContextProvider";
import ColaboradorService from "services/ColaboradorService";
import { SolicitudAusenciaService } from "services";

const ModalProgramarMovimientos = () => {
	const { RangePicker } = DatePicker;
    const { TextArea } = Input;
    const { getUsuarioAutenticado } = useContext(SecurityContext);
	const [form] = Form.useForm();
	const fechasSolicitadas = [];
	const [visibleModal, setVisibleModal] = useAtom(visibleModalCrearSolicitudAusenciaAtom);
	const { fechaLimite, fechaMinimaSolicitud, deshabilitarFechaBloqueada, deshabilitarFueraDeVentana } = useBloqueoMovimientos(visibleModal);
	const { tipoAusencia, limpiarTipoAusencia, onChangeTipoAusencia } = useSeleccionarTipoAusencia();
	const { tiposAusencia } = useListarTiposAusencia();
	const { vacacionesSimuladas, vacacionesSimuladasError, descontarDias, limpiarSimulacion } =
		useSimularDescuentoVacaciones();
	const { jefeInmediato } = useObtenerJefeInmediato();
	const { detalleVacacionesColaborador } = useObtenerDetalleVacacionesColaborador();
	const { esMovil, esDesktop } = useDispositivo();
	const [ocultarFechasSolicitadas, setOcultarFechasSolicitadas] = useAtom(ocultarFechasSolicitadasAtom);
    const { aprobadorPrimerFlujo } = useObtenerAprobadorPrimerNivel();
    const [colaboradores, setColaboradores] = useState(undefined);
    const [loading, setLoading] = useState(false);
    const [solicitudesAusencia, setSolicitudesAusencia] = useAtom(solicitudesAusenciaAtom);
    const isSDF = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20330791684" ? true : false;
    const isSDFEnergia = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20512436090" ? true : false;

	useEffect(() => {
		const cargarDatos = async () => {
			if (visibleModal) {
				form.resetFields();
				limpiarSimulacion();
			}
		};
		cargarDatos();
	}, [visibleModal]);

	useEffect(() => {
		const cargarAprobador = async () => {
			form.setFieldsValue({
				aprobador: tipoAusencia?.requiereAprobacionJefeInmediato
					? jefeInmediato?.nombreCompleto
					: !tipoAusencia?.requiereAprobacionJefeInmediato && tipoAusencia?.requiereAprobacionOtrosCargos
					? tipoAusencia?.aprobadoresOtrosCargos[0].nombreCompleto
					: tipoAusencia?.permitirAprobacionPrimerFlujo
					? aprobadorPrimerFlujo.nombreCompleto//validar si es el que va aprobar y los colaboradores solo los que le fueron asiganados
					: null,
			});
		};
		cargarAprobador();
	}, [tipoAusencia]);

	const deshabilitarFechasSolicitudFin = (current) => {
		if (tipoAusencia?.contabilizarHoras) {
			return moment(current).format("DD/MM/YYYY") < moment(fechasSolicitadas[0]).format("DD/MM/YYYY");
		}
		return current < fechasSolicitadas[0];
	};
	const deshabilitarFechaSolicitudInicio = (current) => {
		return current && current < moment().startOf("day");
	};

	// Un movimiento no puede iniciar en o antes de la fecha limite del bloqueo (aunque el tipo permita fechas anteriores).
	const deshabilitarFechaInicio = (current) =>
		deshabilitarFechaBloqueada(current) ||
		(!tipoAusencia?.permitirSolicitudFechasAnteriores &&
			// Con ventana retroactiva configurada, esta reemplaza a la regla "desde hoy".
			(fechaMinimaSolicitud ? deshabilitarFueraDeVentana(current, false) : !!deshabilitarFechaSolicitudInicio(current)));

    const onClickBtnSolicitar = async (formulario) => {
        const formData = new FormData();
        var identificadoresAdjuntos = [];
        setLoading(true);

		if (ocultarFechasSolicitadas) {
			formulario.fechasSolicitud = [];
			formulario.fechasSolicitud[0] = formulario.informacionAdicional.diaCompensacion;
			formulario.fechasSolicitud[1] = formulario.informacionAdicional.diaCompensacion;
		}

		if (esMovil && !ocultarFechasSolicitadas && !tipoAusencia?.solicitarUnaFecha) {
			formulario.fechasSolicitud = [];
			formulario.fechasSolicitud[0] = formulario.fechaSolicitudInicio;
			formulario.fechasSolicitud[1] = formulario.fechaSolicitudFin;
		}

        if (formulario.adjuntos) {
			for (var posicion = 0; posicion < Object.keys(formulario.adjuntos).length; posicion++) {
				if (Object.values(formulario.adjuntos)[posicion]) {
					formData.append("file", Object.values(formulario.adjuntos)[posicion].file);
					identificadoresAdjuntos.push(Object.keys(formulario.adjuntos)[posicion]);
				}
			}
		}
        
        formData.append(
			"info",
			new Blob(
				[
					JSON.stringify({
						tipoAusenciaId: formulario.tipoAusenciaId,
						fechaInicio: tipoAusencia?.solicitarUnaFecha
                                        ? formulario.fechaEfectiva
                                            ? formulario.fechaEfectiva.format("DD/MM/YYYY HH:mm:ss")
                                            : null
                                        : formulario.fechasSolicitud?.[0]
                                            ? formulario.fechasSolicitud[0].format("DD/MM/YYYY HH:mm:ss")
                                            : null,
						fechaFin: tipoAusencia?.solicitarUnaFecha
                                        ? formulario.fechaEfectiva
                                            ? formulario.fechaEfectiva.format("DD/MM/YYYY HH:mm:ss")
                                            : null
                                        : formulario.fechasSolicitud?.[1]
                                            ? formulario.fechasSolicitud[1].format("DD/MM/YYYY HH:mm:ss")
                                            : null,
						comentario: formulario.comentario,
						informacionAdicional: formulario.informacionAdicional,
                        identificadoresAdjuntos: identificadoresAdjuntos,
                        numeroDocumentoColaborador: formulario.colaborador
					}),
				],
				{
					type: "application/json",
				}
			)
		);

		const response = await SolicitudAusenciaService.registrarSolicitud(formData);

        if (response.estado === "EXITO") {
            setLoading(false);
            form.resetFields();
            setVisibleModal(false);
            setSolicitudesAusencia([...solicitudesAusencia, response.data]);
            message.success("Se ha procesado correctamente su solicitud");
        } else {
            setLoading(false);
            const mensajeModificado = response.mensaje 
            ? response.mensaje.replace(/vacaciones/gi, "tu movimiento")
            : "";
            message.error(mensajeModificado)
            
           // message.error(response.mensaje);
        }
        
        
	};

	const onChangeDatePickerFechaInicio = (fecha) => {
		fechasSolicitadas[0] = fecha;
	};

	const onChangeDatePickerFechaFin = (fecha) => {
		fechasSolicitadas[1] = fecha;
		descontarDias(fechasSolicitadas);
	};

	const FechasSolicitadasExtra = () => {
		if (tipoAusencia?.contabilizarDiasVacaciones) {
			if (vacacionesSimuladas) {
				return (
					<React.Fragment>
						<div
							style={{
								marginTop: "5px",
								display: "flex",
								justifyContent: "space-between",
							}}
						>
							<Tag color="warning">
								<EditOutlined /> Se descontarán de su saldo:
								<span style={{ padding: "0px", fontWeight: "bold" }}>
									{vacacionesSimuladas.diasSolicitados}
								</span>{" "}
								días
							</Tag>
						</div>
						<div style={{ marginTop: "5px" }}>
							<Tag color="blue">
								<CalendarOutlined /> Periodo Vacacional:{" "}
								<span style={{ fontWeight: "bold" }}>
                                {(isSDF || isSDFEnergia)
									? vacacionesSimuladas.periodoVacacionalInicio.substring(6) 
									: vacacionesSimuladas.periodoVacacionalInicio}
								</span>{" "}
								{" - "}
                                <span style={{ fontWeight: "bold" }}>
                                {(isSDF || isSDFEnergia)
									? vacacionesSimuladas.periodoVacacionalFin.substring(6) 
										: vacacionesSimuladas.periodoVacacionalFin}
                                </span>
							</Tag>
						</div>
					</React.Fragment>
				);
			}
			if (vacacionesSimuladasError) {
				return (
					<div
						style={{
							marginTop: "5px",
							display: "flex",
							justifyContent: "space-between",
						}}
					>
						<Tag color="error">
							<span style={{ padding: "0px", fontWeight: "bold" }}>{vacacionesSimuladasError}</span>
						</Tag>
					</div>
				);
			}
		}
		return "";
	};

    const onSearchColaboradores = async (value) => {
        if (value === "") {
            setColaboradores([]);
            return;
        }
        const respuesta = await ColaboradorService.listarPorNombreCompletoODniYAprobador({
            filtro: value.trim(),
        });
        setColaboradores(respuesta.colaboradores);
    };

	return (
        <Modal
            title={(isSDF || isSDFEnergia) ? "Registrar Solicitud Movimiento":"Registrar Solicitud Ausencia"}
            bodyStyle={{ overflowY: "auto", minHeight: "calc(100vh - 220px)", maxHeight: "calc(100vh - 220px)" }}
            width={600}
            centered
            open={visibleModal}
            onCancel={() => {
                setVisibleModal(false);
                form.resetFields();
                limpiarTipoAusencia();
            }}
            onOk={() => form.submit()}
            okText={"Solicitar"}
            confirmLoading={loading}
            okButtonProps={{
                disabled: vacacionesSimuladasError ? true : false,
            }}
        >
            {tipoAusencia?.contabilizarDiasVacaciones && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: "20px", fontWeight: "bold", color: "#108ee9", textAlign: "center" }}>
                        {tipoAusencia?.ocultarDiasTruncos
                            ? detalleVacacionesColaborador?.vacacionesDisponiblesConDiasTruncos + " DÍAS"
                            : detalleVacacionesColaborador?.vacacionesDisponibles + " DÍAS"}
                    </span>
                    <span style={{ fontSize: "13px", textAlign: "center" }}>disponibles</span>
                </div>
            )}
            <Form form={form} layout={"vertical"} onFinish={onClickBtnSolicitar} style={{ padding: "0px 5%" }}>
                <AvisoBloqueoMovimientos fechaLimite={fechaLimite} />
                <Form.Item
                    label={(isSDF || isSDFEnergia) ?"Tipo Movimiento":"Tipo Ausencia"}
                    name={"tipoAusenciaId"}
                    labelAlign="left"
                    rules={[{ required: true, message: "Seleccione el tipo" }]}
                >
                    <Select style={{ width: "100%" }} onChange={onChangeTipoAusencia}>
                        {tiposAusencia !== undefined &&
                            tiposAusencia.map((tipo) => {
                                return (
                                    <Select.Option key={tipo.id} value={tipo.id}>
                                        {tipo.descripcion}
                                    </Select.Option>
                                );
                            })}
                    </Select>
                </Form.Item>

                {tipoAusencia?.controles &&
                    tipoAusencia.controles.map((info) => {
                        return <Control key={info.key} info={info} />;
                    })}

                {tipoAusencia?.archivos &&
                    tipoAusencia.archivos.map((info) => {
                        return <Archivo key={info.key} info={info} />;
                    })}

            <Form.Item
                label="Colaborador"
                name="colaborador"
                rules={[{ required: true, message: "Ingrese al colaborador" }]}
            >
                <Select
                    style={{ width: "100%" }}
                    allowClear
                    showSearch
                    placeholder="Ingrese el nombre del colaborador"
                    onSearch={onSearchColaboradores}
                    filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                >
                    {colaboradores !== undefined &&
                        colaboradores?.map((colaborador) => (
                            <Select.Option
                                key={colaborador.numeroDocumentoIdentidad}
                                value={colaborador.numeroDocumentoIdentidad}
                            >
                                {colaborador.nombreCompleto}
                            </Select.Option>
                        ))}
                </Select>
                </Form.Item>
                
                {tipoAusencia?.solicitarUnaFecha && (
                    <Form.Item
                        label="Fecha Efectiva"
                        name="fechaEfectiva"
                        rules={[
                            {
                                required: true,
                                message: "Ingrese la fecha",
                            },
                        ]}
                    >
                        <DatePicker style={{ width: "100%" }}
                            allowClear={true}
                             disabledDate={deshabilitarFechaInicio}
                            format={tipoAusencia?.contabilizarHoras ? "DD/MM/YYYY hh:mm a" : "DD/MM/YYYY"}
                            showTime={tipoAusencia?.contabilizarHoras ? true : false}
                        />
                        
                    </Form.Item>
                )}
                {esDesktop && !tipoAusencia?.solicitarUnaFecha && !ocultarFechasSolicitadas && (
                    <Form.Item
                        label={
                            <div>
                                <CalendarOutlined /> Fechas Solicitadas
                            </div>
                        }
                        name="fechasSolicitud"
                        labelAlign="left"
                        rules={[{ required: true, message: "Ingrese la fecha" }]}
                        extra={<FechasSolicitadasExtra />}
                    >
                        <RangePicker
                            allowClear={true}
                            format={tipoAusencia?.contabilizarHoras ? "DD/MM/YYYY hh:mm a" : "DD/MM/YYYY"}
                            showTime={tipoAusencia?.contabilizarHoras ? true : false}
                            disabledDate={deshabilitarFechaInicio}
                            onChange={(fechasSolicitadas) => {
                                descontarDias(fechasSolicitadas);
                            }}
                            style={{ width: "100%" }}
                        />
                    </Form.Item>
                )}

                {esMovil && !tipoAusencia?.solicitarUnaFecha && !ocultarFechasSolicitadas && (
                    <React.Fragment>
                        <Form.Item
                            label={
                                <div>
                                    <CalendarOutlined /> Fecha Inicio
                                </div>
                            }
                            name="fechaSolicitudInicio"
                            labelAlign="left"
                            rules={[{ required: true, message: "Ingrese la la fecha inicio" }]}
                        >
                            <DatePicker
                                allowClear={true}
                                disabledDate={deshabilitarFechaInicio}
                                onChange={onChangeDatePickerFechaInicio}
                                format={tipoAusencia?.contabilizarHoras ? "DD/MM/YYYY hh:mm a" : "DD/MM/YYYY"}
                                showTime={tipoAusencia?.contabilizarHoras ? true : false}
                                style={{ width: "100%" }}
                            />
                        </Form.Item>

                        <Form.Item
                            label={
                                <div>
                                    <CalendarOutlined /> Fecha Fin
                                </div>
                            }
                            name="fechaSolicitudFin"
                            labelAlign="left"
                            rules={[{ required: true, message: "Ingrese la la fecha fin" }]}
                            extra={<FechasSolicitadasExtra />}
                        >
                            <DatePicker
                                allowClear={true}
                                onChange={onChangeDatePickerFechaFin}
                                format={tipoAusencia?.contabilizarHoras ? "DD/MM/YYYY hh:mm a" : "DD/MM/YYYY"}
                                showTime={tipoAusencia?.contabilizarHoras ? true : false}
                                disabledDate={deshabilitarFechasSolicitudFin}
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                    </React.Fragment>
                )}

                <Form.Item label="Comentario" name="comentario" labelAlign="left">
                    <TextArea autoSize={{ minRows: 2, maxRows: 5 }} showCount maxLength={250} />
                </Form.Item>

                {/* <Form.Item label="Aprobador" name="aprobador">
                    <Input readOnly />
                </Form.Item> */}
            </Form>
        </Modal>
	);
};
export default ModalProgramarMovimientos;
